import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Eye, EyeOff, Mail, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { AppFooter } from '@/components/layout/AppFooter';
import { BrandLogo } from '@/components/BrandLogo';
import api from '@/lib/api';

export default function SignupPage() {
  const [form, setForm] = useState({ email: '', password: '', first_name: '', last_name: '', company_name: '', accepted_terms: false });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState(1); // 1 = form, 2 = verify email
  const [verificationCode, setVerificationCode] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password || !form.company_name || !form.first_name) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!form.accepted_terms) {
      toast.error('You must accept the Terms of Service and Privacy Policy');
      return;
    }
    
    // Send verification email
    setSendingCode(true);
    try {
      await api.post('/auth/send-verification', { email: form.email });
      toast.success('Verification code sent to your email!');
      setStep(2); // Move to verification step
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyAndRegister = async (e) => {
    e.preventDefault();
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter the 6-digit code from your email');
      return;
    }
    
    setVerifying(true);
    try {
      // Verify email first
      console.log('Verifying email:', form.email, 'with code:', verificationCode);
      await api.post('/auth/verify-email', { email: form.email, code: verificationCode });
      console.log('Email verified successfully');
      
      // Email verified, now create account
      setLoading(true);
      console.log('Creating account...');
      await signup(form);
      console.log('Account created successfully');
      toast.success('Account created! Choose your plan to get started.');
      navigate('/pricing');
    } catch (err) {
      console.error('Verification/Registration error:', err);
      console.error('Error response:', err.response?.data);
      const errorMsg = err.response?.data?.detail || 'Verification or registration failed';
      toast.error(errorMsg);
    } finally {
      setVerifying(false);
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setSendingCode(true);
    try {
      await api.post('/auth/send-verification', { email: form.email });
      toast.success('New code sent!');
    } catch (err) {
      toast.error('Failed to resend');
    } finally {
      setSendingCode(false);
    }
  };

  const update = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  return (
    <div data-testid="signup-page" className="min-h-screen flex items-center justify-center bg-background p-6">
      <Card className="w-full max-w-[460px] shadow-[var(--shadow-soft)]">
        <CardHeader className="text-center space-y-1">
          <button type="button" onClick={() => navigate('/')} data-testid="signup-logo-button" aria-label="Back to homepage" className="flex justify-center mb-4 mx-auto cursor-pointer bg-transparent border-0 p-0">
            <BrandLogo width={180} />
          </button>
          <CardTitle className="text-2xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            {step === 1 ? 'Create your account' : 'Verify your email'}
          </CardTitle>
          <CardDescription>
            {step === 1 ? 'Start managing vendor compliance in minutes' : `We sent a code to ${form.email}`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === 1 ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div><Label>Company Name *</Label><Input data-testid="signup-company-input" value={form.company_name} onChange={update('company_name')} placeholder="Acme Property Management" required /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>First Name *</Label><Input data-testid="signup-firstname-input" value={form.first_name} onChange={update('first_name')} required /></div>
                <div><Label>Last Name</Label><Input data-testid="signup-lastname-input" value={form.last_name} onChange={update('last_name')} /></div>
              </div>
              <div><Label>Email *</Label><Input data-testid="signup-email-input" type="email" value={form.email} onChange={update('email')} placeholder="you@company.com" required /></div>
              <div>
                <Label>Password *</Label>
                <div className="relative">
                  <Input data-testid="signup-password-input" type={showPassword ? "text" : "password"} value={form.password} onChange={update('password')} placeholder="Min 6 characters" required className="pr-10" />
                  <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Checkbox id="terms" data-testid="signup-terms-checkbox" checked={form.accepted_terms} onCheckedChange={v => setForm(f => ({...f, accepted_terms: !!v}))} className="mt-0.5" />
                <Label htmlFor="terms" className="text-xs leading-relaxed text-muted-foreground cursor-pointer">
                  I agree to the <Link to="/terms" target="_blank" className="text-primary hover:underline">Terms of Service</Link> and <Link to="/privacy" target="_blank" className="text-primary hover:underline">Privacy Policy</Link>
                </Label>
              </div>
              <Button type="submit" data-testid="signup-submit-button" className="w-full" disabled={sendingCode || !form.accepted_terms}>
                {sendingCode ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending code...</> : <><Mail className="mr-2 h-4 w-4" /> Send Verification Code</>}
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                Already have an account? <Link to="/login" className="text-primary hover:underline">Log in</Link>
              </p>
            </form>
          ) : (
            <form onSubmit={handleVerifyAndRegister} className="space-y-4">
              <div className="text-center py-4">
                <div className="flex h-16 w-16 mx-auto items-center justify-center rounded-full bg-primary/10 mb-4">
                  <Mail className="h-8 w-8 text-primary" />
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Enter the 6-digit code we sent to <strong>{form.email}</strong>
                </p>
              </div>
              
              <div>
                <Label>Verification Code</Label>
                <Input 
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  className="text-center text-2xl tracking-widest font-mono"
                  data-testid="signup-verification-code"
                  autoFocus
                />
              </div>

              <Button type="submit" className="w-full" disabled={verifying || loading || verificationCode.length !== 6}>
                {(verifying || loading) ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
                {(verifying || loading) ? 'Verifying & Creating Account...' : 'Verify & Create Account'}
              </Button>

              <div className="flex justify-between text-xs">
                <button type="button" onClick={() => setStep(1)} className="text-muted-foreground hover:text-foreground">
                  ← Back to signup
                </button>
                <button type="button" onClick={handleResendCode} disabled={sendingCode} className="text-primary hover:underline">
                  {sendingCode ? 'Sending...' : 'Resend code'}
                </button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
